import { type ClientContext, ContextClient } from '~/services/contextClient';
import ExternalListsClientFactory from './externalListsClient.factory';
import type {
    ExternalListsClientInterface,
    ExternalList,
    ExternalListAttribute,
    GetExternalListsParams,
    ExternalListsContactCountParams,
    OrgUnitExternalListsContactCountParams,
    GroupExternalListsContactCountParams,
    ExternalListsAttributesParams,
    ExternalListsGroupCountParams,
    ExternalListTemplateConfig,
    ExternalListTemplateConfigPayload,
} from './externalListsClient.types';
import type { CursorPaginationResponse } from '~/services/api';
import type { AxiosPromise } from 'axios';

class ExternalListsClient extends ContextClient<ExternalListsClientInterface> {
    constructor() {
        super(ExternalListsClientFactory);
    }

    getExternalLists(
        context: ClientContext,
        params?: GetExternalListsParams
    ): AxiosPromise<CursorPaginationResponse<ExternalList>> {
        const client = this.getClient(context);
        return client.getExternalLists(params);
    }

    getExternalListsCount(context: ClientContext, params?: GetExternalListsParams): AxiosPromise<{ count: number }> {
        const client = this.getClient(context);
        return client.getExternalListsCount(params);
    }

    getExternalListsContactCount(
        context: ClientContext,
        params:
            | ExternalListsContactCountParams
            | OrgUnitExternalListsContactCountParams
            | GroupExternalListsContactCountParams
    ): AxiosPromise<{ counts: Record<number, number> }> {
        const client = this.getClient(context);
        return client.getExternalListsContactCount(params);
    }

    getExternalListsAttributes(
        context: ClientContext,
        params: ExternalListsAttributesParams
    ): AxiosPromise<{ data: ExternalListAttribute[] }> {
        const client = this.getClient(context);
        if (!client.getExternalListsAttributes) {
            throw new Error(`getExternalListsAttributes is not supported by ${client.constructor.name}`);
        }
        return client.getExternalListsAttributes(params);
    }

    getExternalListsGroupCount(
        context: ClientContext,
        params: ExternalListsGroupCountParams
    ): AxiosPromise<{ counts: Record<number, number> }> {
        const client = this.getClient(context);
        if (!client.getExternalListsGroupCount) {
            throw new Error(`getExternalListsGroupCount is not supported by ${client.constructor.name}`);
        }
        return client.getExternalListsGroupCount(params);
    }

    getExternalListTemplateConfig(
        context: ClientContext,
        externalListId: number
    ): AxiosPromise<ExternalListTemplateConfig> {
        const client = this.getClient(context);
        if (!client.getExternalListTemplateConfig) {
            throw new Error(`getExternalListTemplateConfig is not supported by ${client.constructor.name}`);
        }
        return client.getExternalListTemplateConfig(externalListId);
    }

    upsertExternalListTemplateConfig(
        context: ClientContext,
        externalListId: number,
        data: ExternalListTemplateConfigPayload
    ): AxiosPromise<ExternalListTemplateConfig> {
        const client = this.getClient(context);
        if (!client.upsertExternalListTemplateConfig) {
            throw new Error(`upsertExternalListTemplateConfig is not supported by ${client.constructor.name}`);
        }
        return client.upsertExternalListTemplateConfig(externalListId, data);
    }

    deleteExternalListTemplateConfig(context: ClientContext, externalListId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        if (!client.deleteExternalListTemplateConfig) {
            throw new Error(`deleteExternalListTemplateConfig is not supported by ${client.constructor.name}`);
        }
        return client.deleteExternalListTemplateConfig(externalListId);
    }
}

export default new ExternalListsClient();
